import React, { Suspense, useEffect, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import {
  BadgePercent,
  CalendarRange,
  CheckCircle2,
  Ellipsis,
  FileText,
  Info,
  Landmark,
  LayoutGrid,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Scale,
  Settings,
  ShieldCheck,
  Sun,
  X,
} from "lucide-react";
import { useApp } from "../../state/AppState";
import { BrandMark, cx } from "../ui/Primitives";
import { Dialog } from "../ui/Dialog";
import { PageSkeleton } from "./PageSkeleton";
import ErrorBoundary from "./ErrorBoundary";

export const NAV = [
  { section: "Plan" },
  { to: "/", label: "Overview", short: "Overview", icon: LayoutGrid, match: (p) => p === "/" },
  { to: "/loans", label: "My Loans", short: "Loans", icon: Landmark, match: (p) => p.startsWith("/loans") },
  { to: "/planner", label: "Repayment Planner", short: "Planner", icon: CalendarRange, match: (p) => p.startsWith("/planner") },
  { section: "Analyse" },
  {
    to: "/investments",
    label: "Investment Comparison",
    short: "Compare",
    icon: Scale,
    match: (p) => p.startsWith("/investments") || p.startsWith("/goal-tracking"),
  },
  { to: "/inflation", label: "Inflation Analysis", short: "Inflation", icon: BadgePercent, match: (p) => p.startsWith("/inflation") },
  { section: "Manage" },
  { to: "/reports", label: "Reports", short: "Reports", icon: FileText, match: (p) => p.startsWith("/reports") },
  { to: "/settings", label: "Settings", short: "Settings", icon: Settings, match: (p) => p.startsWith("/settings") },
];

const LINKS = NAV.filter((n) => n.to);
const TAB_IDS = ["/", "/loans", "/planner", "/investments"];

function ThemeButton({ className, withLabel }) {
  const { resolvedTheme, updateSettings } = useApp();
  const dark = resolvedTheme === "dark";
  const label = dark ? "Switch to light theme" : "Switch to dark theme";
  return (
    <button
      type="button"
      className={className}
      onClick={() => updateSettings({ theme: dark ? "light" : "dark" })}
      aria-label={label}
      title={label}
    >
      {dark ? <Sun aria-hidden="true" /> : <Moon aria-hidden="true" />}
      {withLabel && <span className="sidebar__footer-label">{dark ? "Light theme" : "Dark theme"}</span>}
    </button>
  );
}

function Sidebar({ pathname }) {
  const { settings, updateSettings } = useApp();
  const collapsed = settings.sidebarCollapsed;
  return (
    <aside className="sidebar" aria-label="Primary">
      <Link to="/" className="brand" aria-label="Loan Recovery Pro home">
        <BrandMark />
        <span className="brand__text">
          <span className="brand__name">Loan Recovery Pro</span>
          <span className="brand__tag">Debt &amp; wealth planner</span>
        </span>
      </Link>
      <nav className="nav">
        {NAV.map((item) =>
          item.section ? (
            <div className="nav__section" key={item.section}>
              {item.section}
            </div>
          ) : (
            <Link
              key={item.to}
              to={item.to}
              className="nav__link"
              aria-current={item.match(pathname) ? "page" : undefined}
              title={collapsed ? item.label : undefined}
            >
              <item.icon aria-hidden="true" />
              <span className="nav__label">{item.label}</span>
            </Link>
          )
        )}
      </nav>
      <div className="sidebar__footer">
        <ThemeButton className="sidebar__btn" withLabel />
        <button
          type="button"
          className="sidebar__btn"
          onClick={() => updateSettings({ sidebarCollapsed: !collapsed })}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-expanded={!collapsed}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <PanelLeftOpen aria-hidden="true" /> : <PanelLeftClose aria-hidden="true" />}
          <span className="sidebar__footer-label">Collapse</span>
        </button>
      </div>
    </aside>
  );
}

function MobileChrome({ pathname }) {
  const [moreOpen, setMoreOpen] = useState(false);
  useEffect(() => setMoreOpen(false), [pathname]);
  const tabs = LINKS.filter((l) => TAB_IDS.includes(l.to));
  const moreActive = !tabs.some((t) => t.match(pathname));
  const moreLinks = [
    ...LINKS.filter((l) => !TAB_IDS.includes(l.to)),
    { to: "/features", label: "About", icon: Info, match: (p) => p.startsWith("/features") },
    { to: "/privacy-policy", label: "Privacy", icon: ShieldCheck, match: (p) => p.startsWith("/privacy") },
  ];

  return (
    <>
      <header className="topbar">
        <Link to="/" className="brand" aria-label="Loan Recovery Pro home">
          <BrandMark />
          <span className="brand__text">
            <span className="brand__name">Loan Recovery Pro</span>
          </span>
        </Link>
        <ThemeButton className="btn btn--ghost btn--icon" />
      </header>
      <nav className="tabbar" aria-label="Primary">
        {tabs.map((t) => (
          <Link key={t.to} to={t.to} className="tabbar__item" aria-current={t.match(pathname) ? "page" : undefined}>
            <t.icon aria-hidden="true" />
            {t.short}
          </Link>
        ))}
        <button
          type="button"
          className={cx("tabbar__item", moreActive && "is-active")}
          onClick={() => setMoreOpen(true)}
          aria-haspopup="dialog"
        >
          <Ellipsis aria-hidden="true" />
          More
        </button>
      </nav>
      <Dialog open={moreOpen} onClose={() => setMoreOpen(false)} title="More" labelledBy="more-title">
        <nav className="sheet-nav" aria-label="More pages">
          {moreLinks.map((l) => (
            <Link key={l.to} to={l.to} aria-current={l.match(pathname) ? "page" : undefined}>
              <l.icon aria-hidden="true" />
              {l.short || l.label}
            </Link>
          ))}
        </nav>
      </Dialog>
    </>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <span>
        © {new Date().getFullYear()} Loan Recovery Pro · By{" "}
        <a href="https://maheshthorat.web.app" target="_blank" rel="noreferrer">
          Mahesh Mohan Thorat
        </a>{" "}
        · Made in India
      </span>
      <span className="footer__links">
        <span>Estimates for planning, not financial advice.</span>
        <Link to="/features">About</Link>
        <Link to="/privacy-policy">Privacy Policy</Link>
      </span>
    </footer>
  );
}

function Toasts() {
  const { toasts, dismissToast } = useApp();
  return (
    <div className="toasts" aria-live="polite" aria-atomic="false">
      {toasts.map((t) => (
        <div className="toast" key={t.id} role="status">
          <span className="toast__icon" aria-hidden="true">
            <CheckCircle2 />
          </span>
          <span className="toast__text">{t.message}</span>
          {t.action && (
            <button
              type="button"
              className="btn btn--soft btn--sm"
              onClick={() => {
                t.action.onClick();
                dismissToast(t.id);
              }}
            >
              {t.action.label}
            </button>
          )}
          <button type="button" className="btn btn--ghost btn--icon btn--sm" onClick={() => dismissToast(t.id)} aria-label="Dismiss">
            <X />
          </button>
        </div>
      ))}
    </div>
  );
}

export default function Shell() {
  const { settings } = useApp();
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <div className={cx("app", settings.sidebarCollapsed && "is-collapsed")}>
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <Sidebar pathname={pathname} />
      <div className="main">
        <MobileChrome pathname={pathname} />
        <main id="main" className="content" tabIndex={-1}>
          <ErrorBoundary resetKey={pathname}>
            <Suspense fallback={<PageSkeleton />}>
              <div className="page" key={pathname}>
                <Outlet />
              </div>
            </Suspense>
          </ErrorBoundary>
        </main>
        <Footer />
      </div>
      <Toasts />
    </div>
  );
}
